import i18n from 'i18n'
import { all, takeEvery, takeLatest, put, call } from 'redux-saga/effects'
import { infoDB } from 'myStore'
import { getEnvParam } from 'env'
import { isEqual } from 'lodash'
import qs from 'qs'
import { history, store as reduxStore } from 'index'
import settingSyncService, { SchemaValidationError } from 'services/settingSync'
import { CALENDAR_EXCLUDED_KEYS, TODO_EXCLUDED_KEYS } from './settingMapping'
import { stringToHash, basicColors } from 'utils/ui'
import { getAvailableLocale } from 'localizationUtils'
import { AccountSettings, initialState } from './types'
import actions from './actions'
import {
  ChangeSettingPayload,
  SetThemePayload,
  SetLocalePayload,
  ReloadSettingsPayload,
  SettingsState,
  LOCAL_KEY_ROUTING,
  SYNC_KEY_ROUTING,
  settingsVersion,
  MigrationAccessor,
  isValidSettingsCategory,
} from './types'

const settingsInfo: { userName?: string } = {}

/** history.listen 해제 함수 (재등록 시 누적 방지) */
let _unlistenHistory: (() => void) | null = null

/**
 * IndexedDB에서 설정을 로드하고 기본값과 머지한다.
 * reducers.ts(초기 state)와 RELOAD_SETTINGS saga에서 공통 사용.
 */
export const loadSettingsFromDB = async (storedSettings: SettingsState): Promise<SettingsState> => {
  const settings: Record<string, unknown> = {}

  const keys = Object.keys(storedSettings) as Array<keyof SettingsState & string>
  for (const key of keys) {
    if (key === 'localSettings') continue
    const item: unknown = await infoDB.getItem(getSettingKey(key))
    if (item != null && typeof item === 'object') {
      const initialData = storedSettings[key] as Record<string, unknown>
      const merged = { ...(item as object) }
      Object.keys(initialData).forEach((subKey) => {
        if ((merged as Record<string, unknown>)[subKey] === undefined) {
          ;(merged as Record<string, unknown>)[subKey] = initialData[subKey]
        }
      })
      settings[key] = merged
    } else {
      settings[key] = item != null ? item : storedSettings[key]
    }
  }

  const storedLocal = await infoDB.getItem(getSettingKey('localSettings'))
  if (storedLocal != null && typeof storedLocal === 'object') {
    const dbLocal = storedLocal as Record<string, Record<string, unknown>>
    const defaultLocal = storedSettings.localSettings
    settings.localSettings = {
      email: { ...defaultLocal.email, ...(dbLocal.email ?? {}) },
      contacts: { ...defaultLocal.contacts, ...(dbLocal.contacts ?? {}) },
      calendar: { ...defaultLocal.calendar, ...(dbLocal.calendar ?? {}) },
      todo: { ...defaultLocal.todo, ...(dbLocal.todo ?? {}) },
    }
  } else {
    settings.localSettings = {
      ...storedSettings.localSettings,
    }
  }

  return settings as SettingsState
}

export const settingsMigration = async <T>(
  target: T,
  accessor: MigrationAccessor<T>,
): Promise<void> => {
  if (target == null) return
  if (accessor.type === 'server') {
    if (Object.keys(target as object).length === 0) return // 서버 데이터가 비어있으면 처리하지 않음.
  }

  const prevVersionRet = await accessor.getValue(target, 'version')
  const prevVersion = typeof prevVersionRet === 'number' ? (prevVersionRet as number) : 0

  // v0 → v1: editorFont 개별 키를 그룹 키로 통합 (DB 전용: 서버 데이터에서는 prevVersion != null이므로 스킵)
  if (prevVersion == 0) {
    const editorFont: Record<string, unknown> = {}
    const editorFontList = [
      'editorFontFamily',
      'editorFontSizeString',
      'editorFontColorReFwd',
      'editorFontColorNew',
    ]
    for (const key of editorFontList) {
      const item = await accessor.getValue(target, key)
      if (item != null) {
        editorFont[key] = item
        await accessor.removeValue(target, key)
      }
    }
    if (Object.keys(editorFont).length > 0) {
      await accessor.setValue(target, 'editorFont', editorFont)
    }
  }

  // v1 → v2: local 필드를 localSettings 통합 키로 이동 + flat sync keys를 카테고리로
  if (prevVersion < 2) {
    if (accessor.type === 'server') {
      const calendarData = await accessor.getValue(target, 'calendar')
      // server에서 오는 데이터는 version 2 이전인 경우,
      // 1. icsSubscriptions 이외의 데이터는 모두 유효하지 않은 것으로 간주한다.
      // 그러므로 'server' type인 경우, target은 object이므로, 하위의 모든 key를 제거한다.
      Object.keys(target as object).forEach((key) => {
        delete (target as Record<string, unknown>)[key]
      })
      if (calendarData != null && typeof calendarData === 'object') {
        const calObj = calendarData as Record<string, unknown>
        // 1-1. calendar.icsSubscriptions 가  있으면 이 부분만 server setting으로 유지한다.
        if (calObj.icsSubscriptions != null) {
          await accessor.setValue(target, 'calendar', { icsSubscriptions: calObj.icsSubscriptions })
        }
      }
    } else {
      // 2. db에 존재하는 데이터는 version 1인 경우, 모두 유효한 데이터로 간주하여 처리한다.
      const localSettings: Record<string, Record<string, unknown>> = {
        account: {},
        email: {},
        contacts: {},
        calendar: {},
        todo: {},
      }

      // 1. calendar에서 local 필드 추출
      const calendarData = await accessor.getValue(target, 'calendar')
      if (calendarData != null && typeof calendarData === 'object') {
        const calObj = calendarData as Record<string, unknown>
        let changed = false
        for (const key of CALENDAR_EXCLUDED_KEYS) {
          if (calObj[key] !== undefined) {
            localSettings.calendar[key] = calObj[key]
            delete calObj[key]
            changed = true
          }
        }
        if (changed) {
          await accessor.setValue(target, 'calendar', calObj)
        }
      }

      // 2. todo에서 local 필드 추출
      const todoData = await accessor.getValue(target, 'todo')
      if (todoData != null && typeof todoData === 'object') {
        const todoObj = todoData as Record<string, unknown>
        let changed = false
        for (const key of TODO_EXCLUDED_KEYS) {
          if (todoObj[key] !== undefined) {
            localSettings.todo[key] = todoObj[key]
            delete todoObj[key]
            changed = true
          }
        }
        if (changed) {
          await accessor.setValue(target, 'todo', todoObj)
        }
      }

      // 3. flat local keys → localSettings.email / localSettings.contacts
      for (const [flatKey, group] of Object.entries(LOCAL_KEY_ROUTING)) {
        const item = await accessor.getValue(target, flatKey)
        if (item != null) {
          localSettings[group][flatKey] = item
          await accessor.removeValue(target, flatKey)
        }
      }

      // 5. legacy 키 → email.viewMode (sync 카테고리로 직접 이전, step 8에서 주입)
      //    - flat 'mailLayout' 우선 (ca0de8c85 이후 v0 사용자)
      //    - 'emailListOnly' fallback (ca0de8c85 이전 v0 사용자)
      //    - 두 키 모두 존재 시 mailLayout이 viewMode 값으로 채택되지만, emailListOnly도 함께 정리
      let viewModeFromList: string | null = null

      const flatMailLayout = await accessor.getValue(target, 'mailLayout')
      if (flatMailLayout != null && typeof flatMailLayout === 'string') {
        viewModeFromList = flatMailLayout
        await accessor.removeValue(target, 'mailLayout')
      }

      const emailListOnly = await accessor.getValue(target, 'emailListOnly')
      if (emailListOnly != null) {
        if (viewModeFromList == null) {
          viewModeFromList = emailListOnly ? 'noSplit' : 'splitHorizontal'
        }
        await accessor.removeValue(target, 'emailListOnly')
      }

      // 6. 기존 localSettings가 있으면 머지
      const existingLocal = await accessor.getValue(target, 'localSettings')
      if (existingLocal != null && typeof existingLocal === 'object') {
        const existing = existingLocal as Record<string, Record<string, unknown>>
        for (const group of Object.keys(localSettings)) {
          if (existing[group] != null) {
            localSettings[group] = { ...existing[group], ...localSettings[group] }
          }
        }
      }

      // 7. localSettings 통합 키 저장
      await accessor.setValue(target, 'localSettings', localSettings)

      // 8. flat sync keys → 카테고리 그룹 키로 마이그레이션
      const syncCategories: Record<string, Record<string, unknown>> = {
        account: {},
        general: {},
        email: {},
      }

      for (const [flatKey, category] of Object.entries(SYNC_KEY_ROUTING)) {
        const item = await accessor.getValue(target, flatKey)
        if (item != null) {
          syncCategories[category][flatKey] = item
          await accessor.removeValue(target, flatKey)
        }
      }
      // 기존 카테고리 그룹 키가 있으면 머지
      for (const category of Object.keys(syncCategories)) {
        const existing = await accessor.getValue(target, category)
        if (existing != null && typeof existing === 'object') {
          syncCategories[category] = {
            ...(existing as Record<string, unknown>),
            ...syncCategories[category],
          }
        }
        // viewMode 특별 처리: email 카테고리에 viewMode가 없을 때만 viewModeFromList 주입
        // (서버에서 받은 viewMode가 마이그레이션 fallback에 덮이지 않도록 보장)
        if (
          category === 'email' &&
          viewModeFromList != null &&
          syncCategories.email.viewMode == null
        ) {
          syncCategories.email.viewMode = viewModeFromList
        }
        if (Object.keys(syncCategories[category]).length > 0) {
          await accessor.setValue(target, category, syncCategories[category])
        }
      }
    }
  }

  // v2 → v3: 서명 키를 localSettings.account → account 로 이전.
  // 헬퍼는 존재검사 기반 멱등이며, server 데이터에는 localSettings가 없어 no-op(version만 갱신)이다.
  // 전환기 버전 핑퐁(구버전 클라이언트가 서버 version을 2로 되돌림)으로 이 블록이 반복 실행될 수
  // 있으므로, 비멱등 로직을 추가하면 안 된다.
  if (prevVersion < 3) {
    await migrateSignatureKeys(target, accessor)
    await accessor.setValue(target, 'version', settingsVersion)
  }
}

const SIGNATURE_KEYS = ['signatureForNew', 'signatureForReplyForward'] as const

const migrateSignatureKeys = async <T>(
  target: T,
  accessor: MigrationAccessor<T>,
): Promise<void> => {
  const localSettings = await accessor.getValue(target, 'localSettings')
  if (localSettings == null || typeof localSettings !== 'object') return
  const local = localSettings as Record<string, Record<string, unknown>>
  const localAccount = local.account
  if (localAccount == null || typeof localAccount !== 'object') return

  const accountRet = await accessor.getValue(target, 'account')
  const account =
    accountRet != null && typeof accountRet === 'object'
      ? { ...(accountRet as Record<string, unknown>) }
      : {}

  let changed = false
  for (const key of SIGNATURE_KEYS) {
    if (localAccount[key] !== undefined) {
      account[key] = localAccount[key]
      delete localAccount[key]
      changed = true
    }
  }
  if (!changed) return

  await accessor.setValue(target, 'account', account)
  await accessor.setValue(target, 'localSettings', local)
}

export function getSettingKey(key: string): string {
  if (settingsInfo.userName == null) {
    const email = getEnvParam('userEmail')
    if (email != null) {
      let userName = email
      if (__ENV_PRODUCTION__) {
        userName = stringToHash(email)
      }
      settingsInfo.userName = userName
    } else {
      return `app.settings.${key}`
    }
  }
  return `${settingsInfo.userName}-settings.${key}`
}

/**
 * settings 변경을 처리하는 saga.
 *
 * payload는 Record<string, unknown> 맵으로, 키는 다음 두 형태를 지원한다:
 *   - dot-path: 'general.locale', 'localSettings.email.theme' 등
 *   - 카테고리 전체: 'calendar', 'ai' 등 (value가 카테고리 전체 객체)
 *
 * 동작 원리:
 *   1. payload의 각 엔트리를 순회하며 카테고리별 버퍼(updatedCategories)에 누적한다.
 *   2. 각 엔트리의 key/value로 트리 객체를 생성한 뒤, Redux 기존 데이터(또는 이전 루프 결과)와
 *      deepMerge하여 updatedCategories에 저장한다.
 *   3. dot-path 키든 카테고리 전체 교체든, 항상 기존 데이터 위에 merge되므로
 *      나머지 키가 보존된다.
 *   4. 모든 엔트리 처리 후, 변경된 카테고리만 DB에 저장하고 단일 SET_STATE로 dispatch한다.
 */
export function* CHANGE_SETTING(action: {
  type?: string
  payload: ChangeSettingPayload
  onComplete?: () => void
}) {
  const entries = action.payload
  const settings = (reduxStore.getState() as { settings: SettingsState }).settings

  // 카테고리별 버퍼: 기존 데이터 위에 변경사항을 누적
  const updatedCategories: Record<string, unknown> = {}

  // merge가 아니라 replace로 동작해야 하는 key는 버퍼에 미리 준비한다 (Redux state 직접 변경 방지).
  const accountSettings = entries?.account as AccountSettings
  if (accountSettings != null) {
    const accountBase = { ...settings.account } as Record<string, unknown>
    if (accountSettings.alwaysCc != null) {
      accountBase.alwaysCc = {}
    }
    if (accountSettings.alwaysBcc != null) {
      accountBase.alwaysBcc = {}
    }
    updatedCategories.account = accountBase
  }

  const deepMerge = (
    target: Record<string, unknown>,
    source: Record<string, unknown>,
  ): Record<string, unknown> => {
    const result = { ...target }
    for (const [k, srcVal] of Object.entries(source)) {
      const tgtVal = result[k]
      if (
        tgtVal != null &&
        srcVal != null &&
        typeof tgtVal === 'object' &&
        typeof srcVal === 'object' &&
        !Array.isArray(tgtVal) &&
        !Array.isArray(srcVal)
      ) {
        result[k] = deepMerge(tgtVal as Record<string, unknown>, srcVal as Record<string, unknown>)
      } else {
        result[k] = srcVal
      }
    }
    return result
  }

  for (const [key, value] of Object.entries(entries)) {
    const segments = key.split('.')
    const category = segments[0]

    // SettingsState에 명시적으로 선언된 카테고리만 처리 (인덱스 시그니처로 인한 임의 키 유입 차단)
    if (!isValidSettingsCategory(category)) {
      console.warn(
        `[CHANGE_SETTING] Ignored unknown settings category: "${category}" (from key "${key}")`,
      )
      continue
    }

    // 버퍼에 없으면 Redux 현재 값으로 초기화
    if (updatedCategories[category] == null) {
      const current: unknown = settings[category]
      updatedCategories[category] =
        current != null && typeof current === 'object'
          ? { ...(current as Record<string, unknown>) }
          : current
    }

    // key/value로 트리 객체를 생성
    const restSegments = segments.slice(1)
    let incoming: unknown
    if (restSegments.length === 0) {
      // 카테고리 전체 값
      incoming = value
    } else {
      // dot-path → 트리 변환
      incoming = {}
      let node = incoming as Record<string, unknown>
      for (let i = 0; i < restSegments.length - 1; i++) {
        node[restSegments[i]] = {}
        node = node[restSegments[i]] as Record<string, unknown>
      }
      node[restSegments[restSegments.length - 1]] = value
    }

    // 기존 데이터와 deepMerge
    const existing = updatedCategories[category]
    if (
      existing != null &&
      incoming != null &&
      typeof existing === 'object' &&
      typeof incoming === 'object' &&
      !Array.isArray(existing) &&
      !Array.isArray(incoming)
    ) {
      updatedCategories[category] = deepMerge(
        existing as Record<string, unknown>,
        incoming as Record<string, unknown>,
      )
    } else {
      updatedCategories[category] = incoming
    }
  }

  // 변경된 카테고리만 DB에 저장하고, 단일 SET_STATE로 Redux에 반영
  const statePayload: Record<string, unknown> = {}

  for (const [category, data] of Object.entries(updatedCategories)) {
    const saveCategory = async (): Promise<void> => {
      // 나중에 좀더 자세히 손보자.
      if (category === 'version') {
        await infoDB.setItem(getSettingKey(category), settingsVersion)
      } else {
        await infoDB.setItem(getSettingKey(category), data)
      }
    }
    if (isEqual(settings[category], data) !== true) {
      yield saveCategory()
      statePayload[category] = data
    }
  }

  if (Object.keys(statePayload).length > 0) {
    yield put({
      type: 'settings/SET_STATE',
      payload: statePayload,
    })
  }

  if (action.onComplete != null) {
    action.onComplete()
  }
}

export function* SET_THEME(action: { type: string; payload: SetThemePayload }) {
  const {
    payload: { theme },
  } = action
  const nextTheme = theme === 'dark' ? 'dark' : 'default'
  yield document.querySelector('html')?.setAttribute('data-kit-theme', nextTheme)
  yield put({
    type: 'settings/CHANGE_SETTING',
    payload: { 'localSettings.email.theme': nextTheme },
  })
}

export function* SET_LOCALE(action: {
  type: string
  payload: SetLocalePayload
  onComplete?: () => void
}) {
  const {
    payload: { locale },
  } = action
  const availableLocale = getAvailableLocale(locale)
  const t = yield call(i18n.changeLanguage, availableLocale)
  yield CHANGE_SETTING({
    payload: { 'general.locale': availableLocale },
  })
  if (t != null) {
    yield put({ type: 'menu/RELOAD' })
  }

  if (action.onComplete != null) {
    action.onComplete()
  }
}

export function* SETUP() {
  const changeSettings = (search: string): void => {
    const query = qs.parse(search, { ignoreQueryPrefix: true })
    Object.keys(query).forEach((key) => {
      let value: string | boolean | null | undefined
      switch (query[key]) {
        case 'false':
          value = false
          break
        case 'true':
          value = true
          break
        default:
          value = query[key] as string | null | undefined
          break
      }
      if (key === 'theme') {
        reduxStore.dispatch({ type: 'settings/SET_THEME', payload: { theme: value } })
        return
      }
      if (key === 'locale' || key === 'lang') {
        reduxStore.dispatch({ type: 'settings/SET_LOCALE', payload: { locale: value } })
        return
      }
    })
  }
  // 재등록 전 이전 리스너 해제 (RELOAD_SETTINGS → SETUP 재호출 시 누적 방지)
  if (_unlistenHistory) {
    _unlistenHistory()
    _unlistenHistory = null
  }

  yield changeSettings(history.location.search)
  _unlistenHistory = (yield history.listen((sparams: Location) => {
    const { search } = sparams
    changeSettings(search)
  })) as () => void

  const initTheme = async (): Promise<void> => {
    const { search } = history.location
    const query = qs.parse(search, { ignoreQueryPrefix: true })
    // localSettings 통합 키에서 theme 로드 (마이그레이션 후 개별 키 삭제됨)
    const storedLocal = (await infoDB.getItem(getSettingKey('localSettings'))) as Record<
      string,
      Record<string, unknown>
    > | null
    const theme = query.theme || storedLocal?.email?.theme || 'default'
    reduxStore.dispatch({ type: 'settings/SET_THEME', payload: { theme } })
  }
  yield initTheme()

  const initLocale = async (): Promise<void> => {
    // general 그룹 키에서 locale 로드 (마이그레이션 후 개별 키 삭제됨)
    const storedGeneral = (await infoDB.getItem(getSettingKey('general'))) as Record<
      string,
      unknown
    > | null
    const locale = storedGeneral?.locale
    if (locale) {
      reduxStore.dispatch({
        type: 'settings/CHANGE_SETTING',
        payload: { 'general.locale': locale },
      })
    }
  }
  yield initLocale()
}

export function* RELOAD_SETTINGS(action: { type: string; payload: ReloadSettingsPayload }) {
  const {
    payload: { name, email },
  } = action
  delete settingsInfo.userName

  const dbAccessor: MigrationAccessor<typeof infoDB> = {
    type: 'db',
    getValue: (target, key) => target.getItem(getSettingKey(key)),
    setValue: (target, key, value) => target.setItem(getSettingKey(key), value),
    removeValue: (target, key) => target.removeItem(getSettingKey(key)),
  }

  const loadWithMigration = async (storedSettings: SettingsState): Promise<SettingsState> => {
    await settingsMigration(infoDB, dbAccessor)
    const loadedSettings = await loadSettingsFromDB(storedSettings)
    if (loadedSettings.version !== settingsVersion) {
      // 마이그레이션이 정상 수행되었다면 도달하지 않아야 하는 경로.
      // DB write 실패나 향후 settingsVersion 증가 시점의 edge case를 감지하기 위한 방어적 경고.
      console.warn(
        `Settings version is not matched. Migrated from version ${loadedSettings.version} to ${settingsVersion}.`,
      )
    }
    return loadedSettings
  }
  const newState: SettingsState = yield call(loadWithMigration, initialState)

  if (name == null && email == null) {
    _resetUploadRetry()

    yield call([infoDB, infoDB.removeItem], getSettingKey('localSettings'))
    yield call([infoDB, infoDB.removeItem], getSettingKey('account'))
    yield call([infoDB, infoDB.removeItem], getSettingKey('email'))
    yield call([infoDB, infoDB.removeItem], getSettingKey('calendar'))
    yield call([infoDB, infoDB.removeItem], getSettingKey('todo'))
    yield call([infoDB, infoDB.removeItem], getSettingKey('ai'))

    const logoutState: Partial<SettingsState> = {}
    if (newState.general != null) {
      logoutState.general = newState.general
    }
    if (newState.version != null) {
      logoutState.version = newState.version
    }
    yield put({
      type: 'settings/SET_STATE',
      payload: { ...logoutState },
    })
  } else {
    yield put({
      type: 'settings/SET_STATE',
      payload: { ...newState },
    })
  }

  yield SETUP()

  const applyLocale = async (locale: string): Promise<void> => {
    const availableLocale = getAvailableLocale(locale)
    i18n.changeLanguage(availableLocale)
  }

  yield call(applyLocale, newState.general.locale)

  if (email != null) {
    if (!newState.account.accountName && email) {
      let displayName = name
      if (!displayName || displayName.length === 0) {
        displayName = email.split('@')[0]
      }

      yield CHANGE_SETTING({
        payload: {
          'account.accountName': displayName,
          'account.accountSenderName': displayName,
          'account.accountColor': basicColors[Math.abs(stringToHash(email)) % basicColors.length],
        },
      })
    }

    if (!newState.localSettings?.email?.defaultSendAsAccount) {
      yield CHANGE_SETTING({
        payload: { 'localSettings.email.defaultSendAsAccount': { name: null, email: email } },
      })
    }
  }
}

const UPLOAD_RETRY_MAX = 5
const UPLOAD_RETRY_DELAY_BLOCKED = 1000
const UPLOAD_RETRY_DELAY_FAILED = 2000

type UploadRetryType = 'blocked' | 'network'

let _uploadRetryTimer: ReturnType<typeof setTimeout> | null = null
let _blockedRetryCount = 0
let _failedRetryCount = 0

const _clearUploadRetryTimer = (): void => {
  if (_uploadRetryTimer != null) {
    clearTimeout(_uploadRetryTimer)
    _uploadRetryTimer = null
  }
}

export const _resetUploadRetry = (): void => {
  _clearUploadRetryTimer()
  _blockedRetryCount = 0
  _failedRetryCount = 0
}

const _scheduleUploadRetry = (delayMs: number, retryType: UploadRetryType): void => {
  _clearUploadRetryTimer()
  _uploadRetryTimer = setTimeout(() => {
    _uploadRetryTimer = null
    reduxStore.dispatch({ type: actions.UPLOAD_SETTINGS, payload: {}, retry: retryType })
  }, delayMs)
}

const _isRetryableError = (error: unknown): boolean => {
  if (error instanceof SchemaValidationError) {
    return false
  }
  return true
}

export function* UPLOAD_SETTINGS(action: {
  payload: Record<string, unknown>
  retry?: UploadRetryType
}) {
  // 서버에서 수신한 설정을 로컬에 적용하는 중이면 일정 시간 후 재시도한다.
  if (settingSyncService.isApplyingServerSettings()) {
    if (action.retry === 'blocked') {
      _blockedRetryCount++
      if (_blockedRetryCount < UPLOAD_RETRY_MAX) {
        _scheduleUploadRetry(UPLOAD_RETRY_DELAY_BLOCKED, 'blocked')
      }
    } else {
      _blockedRetryCount = 0
      _scheduleUploadRetry(UPLOAD_RETRY_DELAY_BLOCKED, 'blocked')
    }
    return
  }

  // update 진입: 대기 중인 재시도 타이머를 해제하고 blocked 카운터를 초기화한다.
  _clearUploadRetryTimer()
  _blockedRetryCount = 0

  const entries = action.payload
  const settings = (reduxStore.getState() as { settings: SettingsState }).settings

  const targetCategories: Record<string, unknown> = {}

  for (const key of Object.keys(entries)) {
    targetCategories[key] = settings[key as keyof SettingsState]
  }

  try {
    yield call(settingSyncService.update, targetCategories)
    _failedRetryCount = 0
  } catch (e) {
    console.error('Error occurred while updating server settings:', e)
    if (_isRetryableError(e)) {
      if (action.retry === 'network') {
        _failedRetryCount++
      }
      if (_failedRetryCount < UPLOAD_RETRY_MAX) {
        _scheduleUploadRetry(UPLOAD_RETRY_DELAY_FAILED, 'network')
      }
    }
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.CHANGE_SETTING, CHANGE_SETTING as any),
    takeEvery(actions.SET_THEME, SET_THEME as any),
    takeEvery(actions.SET_LOCALE, SET_LOCALE as any),
    takeEvery(actions.UPLOAD_SETTINGS, UPLOAD_SETTINGS as any),
    takeLatest(actions.RELOAD_SETTINGS, RELOAD_SETTINGS as any),
    SETUP(),
  ])
}
